import { Link, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const BottomBar = () => {
  const location = useLocation();
  const navRef = useRef(null);
  const blockingLayerRef = useRef(null);

  const tabs = [
    { path: '/products', icon: '📦', label: 'Товары' },
    { path: '/stock-in', icon: '➕', label: 'Приход' },
    // { path: '/china-orders', icon: '🇨🇳', label: 'Китай' },
    { path: '/orders', icon: '📋', label: 'Заказы' },
    { path: '/statistics', icon: '📊', label: 'Статистика' },
  ];

  const handleNavClick = (e) => {
    // Предотвращаем всплытие только если клик не на Link
    if (e.target.tagName !== 'A' && !e.target.closest('a')) {
      e.stopPropagation();
    }
  };

  const handleBlockingLayerClick = (e) => {
    // Полностью блокируем клики на невидимом слое
    e.stopPropagation();
    e.preventDefault();
  };

  const handleLinkClick = (e, targetPath) => {
    // Если уже на этой странице, предотвращаем переход
    const isCurrentPath = location.pathname === targetPath || 
      (targetPath === '/products' && location.pathname.startsWith('/products')) ||
      (targetPath === '/orders' && location.pathname.startsWith('/orders')) ||
      (targetPath === '/china-orders' && location.pathname.startsWith('/china-orders')) ||
      (targetPath === '/statistics' && location.pathname.startsWith('/statistics'));
    
    if (isCurrentPath) {
      e.preventDefault();
    }
    // Не блокируем всплытие, чтобы навигация работала нормально
  };

  // Перехватываем события на фазе capture только для блокирующего слоя
  useEffect(() => {
    const blockingLayer = blockingLayerRef.current;
    const nav = navRef.current;
    
    if (!blockingLayer || !nav) return;

    const handleBlockingCapture = (e) => {
      // Проверяем, не является ли цель события частью навбара
      const target = e.target;
      if (nav.contains(target)) {
        // Если клик на навбаре, не блокируем - позволяем навигации работать
        return;
      }
      // Блокируем все остальные события на блокирующем слое
      e.stopPropagation();
      e.preventDefault();
    };

    // Используем capture фазу для перехвата событий до того, как они достигнут элементов под навбаром
    blockingLayer.addEventListener('click', handleBlockingCapture, true);
    blockingLayer.addEventListener('touchstart', handleBlockingCapture, true);
    blockingLayer.addEventListener('touchend', handleBlockingCapture, true);
    blockingLayer.addEventListener('mousedown', handleBlockingCapture, true);
    blockingLayer.addEventListener('mouseup', handleBlockingCapture, true);

    return () => {
      blockingLayer.removeEventListener('click', handleBlockingCapture, true);
      blockingLayer.removeEventListener('touchstart', handleBlockingCapture, true);
      blockingLayer.removeEventListener('touchend', handleBlockingCapture, true);
      blockingLayer.removeEventListener('mousedown', handleBlockingCapture, true);
      blockingLayer.removeEventListener('mouseup', handleBlockingCapture, true);
    };
  }, []);

  return (
    <>
      {/* Невидимый блокирующий слой под навбаром для предотвращения кликов сквозь него */}
      <div 
        ref={blockingLayerRef}
        className="fixed bottom-0 left-0 right-0 z-[9998] pointer-events-auto"
        style={{ 
          height: 'calc(4rem + env(safe-area-inset-bottom, 0px))',
          touchAction: 'none',
          backgroundColor: 'transparent'
        }}
        onClick={handleBlockingLayerClick}
        onTouchStart={handleBlockingLayerClick}
        onTouchEnd={handleBlockingLayerClick}
        onMouseDown={handleBlockingLayerClick}
        onMouseUp={handleBlockingLayerClick}
      />
      
      <nav 
        ref={navRef}
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-[9999] safe-area-bottom pointer-events-auto"
        onClick={handleNavClick}
        style={{ touchAction: 'manipulation' }}
      >
        <div className="max-w-md mx-auto pb-safe">
          <div className="flex items-center h-16">
            {tabs.map((tab) => {
              let isActive = false;
              
              if (tab.path === '/products') {
                isActive = location.pathname === '/products' || location.pathname.startsWith('/products/');
              } else if (tab.path === '/orders') {
                isActive = location.pathname === '/orders' || location.pathname.startsWith('/orders/');
              } else if (tab.path === '/china-orders') {
                isActive = location.pathname === '/china-orders' || location.pathname.startsWith('/china-orders/');
              } else if (tab.path === '/statistics') {
                isActive = location.pathname === '/statistics' || location.pathname.startsWith('/statistics/');
              } else {
                isActive = location.pathname === tab.path;
              }
              
              return (
                <motion.div
                  key={tab.path}
                  className="flex-1 h-full"
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.1 }}
                >
                  <Link
                    to={tab.path}
                    onClick={(e) => handleLinkClick(e, tab.path)}
                    className={`flex flex-col items-center justify-center h-full relative ${
                      isActive ? 'text-blue-600' : 'text-gray-500'
                    }`}
                  >
                    {isActive && (
                      <motion.div
                        className="absolute top-0 left-0 right-0 mx-auto w-12 h-1 bg-blue-600 rounded-b-full"
                        layoutId="activeTab"
                        initial={false}
                        transition={{
                          type: 'spring',
                          stiffness: 400,
                          damping: 30,
                        }}
                      />
                    )}
                    <motion.div
                      className="relative"
                      animate={{
                        scale: isActive ? 1.1 : 1,
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 400,
                        damping: 25,
                      }}
                    >
                      <span className="text-2xl mb-1 block">{tab.icon}</span>
                    </motion.div>
                    <motion.span
                      className="text-xs font-medium"
                      animate={{
                        opacity: isActive ? 1 : 0.7,
                      }}
                      transition={{
                        duration: 0.2,
                      }}
                    >
                      {tab.label}
                    </motion.span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </nav>
    </>
  );
};

export default BottomBar;

